/// <amd-module name="Wiki/CreateWit/CreateWitMenu" />
import * as React from "react";

import * as Resources from "Wiki/CreateWit/Resources";

import { format } from "VSS/Core/Util/String";
import { LineAttrInfoMap } from "VSS/Features/Markdown/MarkdownRendererContracts";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssKeyboardShortcutService } from "VSS/Platform/KeyboardShortcuts";
import { css, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { Callout } from "VSSUI/Callout";
import { ExpandableButton } from "VSSUI/Components/Button/ExpandableButton";
import { IExpandable } from "VSSUI/Expandable";
import { Icon } from "VSSUI/Icon";
import { ContextualMenu, IMenuItem } from "VSSUI/Menu";
import { KeyCode } from "VSSUI/Util";
import { IOffset, IOrigin, IPoint, Location } from "VSSUI/Utilities/Position";

import {
    ClassNamesConstants,
    embedWorkItemToTheSelectedPlace,
    escapeHTMLChars,
    getSelectionElements,
    hasParentNode,
    isSelectionAllowed,
    shouldShowCreateWitMenu
} from "Wiki/CreateWit/CreateWitUtils";
import { Areas, PerformanceConstants, TelemetryConstants } from "Wiki/ViewCommon/CustomerIntelligenceConstants";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { IColorAndIcon, WorkItemTypeColorAndIcons } from "Wit/Common/WorkItemTypeColorAndIcons";
import { IWorkItemService } from "Wit/WorkItem/Service/Contracts";

const PixelExtension = "px";
const CreateWitMenuShortcutCombo = "mod+shift+b";

interface IWorkItemTypesData {
    workItemTypes?: string[];
    colorAndIcons?: { [name: string]: IColorAndIcon };
}

export interface ICreateWitMenuProps {
    markdownContent: string;
    getMarkdownLinesMapping: () => LineAttrInfoMap;
    /**
     * callback to be called to handle the flow after embedding wit in the content.
     *
     * @param content content with embedded wit
     * @param workItemId work item id
     * @param workItemType work item type
     * @param errorMessage message to show in case of error
     */
    handleEmbedWorkItem: (content: string, workItemId: number, workItemType: string, errorMessage: string) => void;

    /**
     * callback to be called to handle failure in wit embed
     */
    handleEmbedWorkItemFailure: (errorMessage: string) => void;
    hasTemplates: () => boolean;
}

export class CreateWitMenu extends VssComponent<ICreateWitMenuProps> {
    private _witMenuContainerRef = React.createRef<HTMLDivElement>();
    private _witMenuRefContainerRef = React.createRef<HTMLDivElement>();
    private _selectedRange: Range;
    private _selectedText: string;
    private _workItemTypesData: IWorkItemTypesData;
    private _expandableButtonRef = React.createRef<ExpandableButton>();

    constructor(props: ICreateWitMenuProps, context: IVssComponentContext) {
        super(props, context);
        const workItemTypesData = this.context.pageContext
            .getService<IVssContributionService>("IVssContributionService")
            .getData<IWorkItemTypesData>("ms.vss-work-web.new-workitem-data-provider");
        this._workItemTypesData = workItemTypesData || {};
    }

    public componentDidMount(): void {
        super.componentDidMount();
        this.addEventListener(document.body, "mouseup", this._onTextSelected);
        this._registerShortcuts();
    }

    public componentWillUnmount(): void {
        super.componentWillUnmount();
        this._unregisterShortcuts();
    }

    public render(): JSX.Element | null {
        return this._workItemTypesData.workItemTypes ? (
            <>
                <div className={ClassNamesConstants.WitMenuRefContainer} ref={this._witMenuRefContainerRef} />
                <div className={css(ClassNamesConstants.WitMenuContainer, "hidden")} ref={this._witMenuContainerRef} onKeyDown={this._handleKeyDown}>
                    <ExpandableButton
                        iconProps={{ iconName: "Add" }}
                        hideDropdownIcon={true}
                        renderCallout={this._onRenderWorkItemTypesDropdown}
                        text={Resources.NewWorkItem}
                        anchorOrigin={{ horizontal: Location.end, vertical: Location.start }}
                        expandKey={KeyCode.rightArrow}
                        subtle
                        ref={this._expandableButtonRef}
                    >
                        {Icon({
                            key: "dropdown-icon",
                            className: "icon-right",
                            iconName: "ChevronRightMed"
                        })}
                    </ExpandableButton>
                </div>
            </>
        ) : null;
    }

    private _handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
        if (e.keyCode === KeyCode.escape) {
            this._hideCreateWitMenu();
        }
    };

    private _registerShortcuts = (): void => {
        this.context.pageContext
            .getService<IVssKeyboardShortcutService>("IVssKeyboardShortcutService")
            .registerShortcut(Resources.WikiShortcutGroupHeading, {
                combos: [CreateWitMenuShortcutCombo],
                description: Resources.CreateWitShortcutDescription,
                action: () => this._onTextSelected(undefined, true)
            });
    };

    private _unregisterShortcuts = (): void => {
        this.context.pageContext
            .getService<IVssKeyboardShortcutService>("IVssKeyboardShortcutService")
            .unRegisterShortcut(Resources.WikiShortcutGroupHeading, CreateWitMenuShortcutCombo);
    };

    private _onTextSelected = (event?: MouseEvent, shouldSetFocus?: boolean): void => {
        // Processing the event with some delay, because on clicking the selected text,
        // event for mouseup comes before the text selection is cleared. So processing
        // with some delay to give some time to get the text deselected, and then process
        // it based with the logic based on selected text.
        setTimeout(() => {
            const textSelection: Selection | null = window.getSelection();
            // Show create wit menu if
            //      1. there is some text selected
            //      2. selection lies within allowed range
            //      3. click is not on menu items. Its means it already shown.
            //  Else
            //      Hide the menu
            let isClickOnMenuItem = false;
            if (event) {
                isClickOnMenuItem = hasParentNode(event.target as HTMLElement, ClassNamesConstants.WitMenuItems);
            }
            if (
                textSelection &&
                textSelection.toString().trim() !== "" &&
                shouldShowCreateWitMenu(textSelection.getRangeAt(0)) &&
                !isClickOnMenuItem
            ) {
                // Clone the range to make sure selection doesn't change in any event.
                this._selectedRange = textSelection.getRangeAt(0).cloneRange();
                this._selectedText = textSelection.toString();

                // To position the create wit menu on text selection,
                // get the selected text bounding rect range from selection API
                // and use the ref wit menu element to position it in the correct position
                if (!textSelection.isCollapsed && this._witMenuContainerRef.current && this._witMenuRefContainerRef.current) {
                    const witMenuEle = this._witMenuContainerRef.current as HTMLElement;
                    const refEle = this._witMenuRefContainerRef.current as HTMLElement;
                    const selectionBCR = this._selectedRange.getBoundingClientRect();
                    const refRangeBCR = refEle.getBoundingClientRect();
                    witMenuEle.style.top = selectionBCR.top - refRangeBCR.top - 52 + PixelExtension; // this will place ele on top of the selection. Doing -52px to adjust the button height
                    witMenuEle.style.left = selectionBCR.left - refRangeBCR.left + PixelExtension; // this will align the right edges together
                    witMenuEle.classList.remove("hidden");
                    if (shouldSetFocus && this._expandableButtonRef.current) {
                        this._expandableButtonRef.current.focus();
                    }
                }
            } else {
                this._hideCreateWitMenu(event);
            }
        }, 50);
    };

    private _hideCreateWitMenu = (event?: MouseEvent): void => {
        // If mouse down is on the button itself, ignore.
        if (event) {
            const srcElement = event.target as HTMLElement | null;
            if (hasParentNode(srcElement, ClassNamesConstants.WitMenuContainer)) {
                return;
            }
        }
        if (this._witMenuContainerRef.current) {
            this._witMenuContainerRef.current.classList.add("hidden");
        }
    };

    private _onRenderWorkItemTypesDropdown = (
        dropdown: IExpandable,
        dropdownId: string,
        anchorElement?: HTMLElement,
        anchorOffset?: IOffset,
        anchorOrigin?: IOrigin,
        anchorPoint?: IPoint,
        dropdownOrigin?: IOrigin
    ): JSX.Element => {
        return (
            <Callout
                anchorElement={anchorElement}
                anchorOffset={anchorOffset}
                anchorOrigin={anchorOrigin}
                anchorPoint={anchorPoint}
                calloutOrigin={dropdownOrigin}
                blurDismiss={true}
                onDismiss={dropdown.collapse}
                contentShadow={true}
            >
                <ContextualMenu
                    anchorElement={anchorElement}
                    anchorOrigin={{ horizontal: Location.end, vertical: Location.end }}
                    menuProps={{
                        id: "create-new-wit",
                        items: this._getMenuItems()
                    }}
                    menuOrigin={{ horizontal: Location.start, vertical: Location.end }}
                    onDismiss={dropdown.collapse}
                    className={ClassNamesConstants.WitMenuItems}
                />
            </Callout>
        );
    };

    /**
     * Look up the work item color and icon
     */
    private _getWorkItemTypeColorAndIcon(witType: string, colorAndIcons: { [name: string]: IColorAndIcon }): IColorAndIcon {
        // get work item color and icon if it is defined.
        witType = witType.trim().toLowerCase();
        const colorAndIcon = colorAndIcons[witType];
        if (colorAndIcon && colorAndIcon.icon) {
            return WorkItemTypeColorAndIcons.createColorAndIcon(colorAndIcon.color, colorAndIcon.icon);
        } else {
            return WorkItemTypeColorAndIcons.getDefault();
        }
    }

    private _getMenuItems = (): IMenuItem[] => {
        const { workItemTypes, colorAndIcons } = this._workItemTypesData;
        const menus: IMenuItem[] = [];
        if (workItemTypes) {
            workItemTypes.forEach(witType => {
                const item: IMenuItem = {
                    id: witType,
                    text: witType,
                    onActivate: this._onMenuItemClicked
                };
                if (colorAndIcons) {
                    const colorAndIcon = this._getWorkItemTypeColorAndIcon(witType, colorAndIcons);
                    item.iconProps = { className: css("bowtie-icon", colorAndIcon.icon), style: { color: colorAndIcon.color } };
                }
                menus.push(item);
            });
        }
        return menus;
    };

    private _onMenuItemClicked = (menuItem: IMenuItem, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>): void => {
        this._hideCreateWitMenu();
        const witType = menuItem.text;
        if (witType) {
            const service = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
            service.publishEvent(Areas.Wiki, TelemetryConstants.CreateWitMenuClicked, { witType, numberOfCharsSelected: this._selectedText.length });
            const options: any = {
                close: (workItem: any): void => {
                    if (workItem.id !== 0) {
                        let updatedContent: string | undefined = undefined;
                        const pageHasTemplates = this.props.hasTemplates();
                        let failedToEmbedWit = false;
                        const performanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
                        let errorMsg = format(Resources.EmbedWitFailed, workItem.workItemType.name, workItem.id);
                        performanceService.startScenario(PerformanceConstants.WitEmbed, false);
                        const isSelAllowed = isSelectionAllowed(this._selectedRange);
                        let telemetryProp: { [key: string]: any } = {
                            witType,
                            witId: workItem.id,
                            pageHasTemplates: pageHasTemplates,
                            numberOfCharsSelected: this._selectedText.length,
                            isSelectionAllowed: isSelAllowed
                        };

                        if (isSelAllowed) {
                            // Trim the last line break selection, because don't want to consider the last line break.
                            if (this._selectedText.charAt(this._selectedText.length - 1) === `\n`) {
                                this._selectedText = this._selectedText.substr(0, this._selectedText.length - 1);
                            }
                            this._addSplitTiming(performanceService, "GetSelectionElementsStarted");
                            const wikiElements = getSelectionElements(this._selectedRange, this._selectedText);
                            this._addSplitTiming(performanceService, "GetSelectionElementsCompleted");
                            const shouldEmbedWit = wikiElements.shouldEmbedWorkItem();
                            telemetryProp = {
                                ...telemetryProp,
                                shouldEmbedWit,
                                peopleMentions: wikiElements.peopleMentions,
                                prMentions: wikiElements.prMentions,
                                witTables: wikiElements.witTables,
                                witMentions: wikiElements.witMentions,
                                ...wikiElements.htmlTags
                            };
                            if (!pageHasTemplates && shouldEmbedWit) {
                                this._addSplitTiming(performanceService, "EmbedWorkItemStarted");
                                updatedContent = embedWorkItemToTheSelectedPlace(
                                    this._selectedRange,
                                    wikiElements,
                                    workItem.id,
                                    this.props.markdownContent,
                                    this.props.getMarkdownLinesMapping()
                                );
                                this._addSplitTiming(performanceService, "EmbedWorkItemCompleted");
                                if (!updatedContent) {
                                    failedToEmbedWit = true;
                                }
                            } else {
                                errorMsg = format(Resources.NoEmbedReason, workItem.workItemType.name, workItem.id);
                            }
                        }
                        if (updatedContent) {
                            this.props.handleEmbedWorkItem(updatedContent, workItem.id, workItem.workItemType.name, errorMsg);
                        } else {
                            this.props.handleEmbedWorkItemFailure(errorMsg);
                        }
                        telemetryProp["failedToEmbedWit"] = failedToEmbedWit;
                        performanceService.endScenario(Areas.Wiki, PerformanceConstants.WitEmbed, false);
                        service.publishEvent(Areas.Wiki, TelemetryConstants.WitCreated, telemetryProp);
                    }
                }
            };
            const initialValues = {
                [CoreFieldRefNames.Title]: this._selectedText.substr(0, 255),
                [CoreFieldRefNames.Description]: escapeHTMLChars(this._selectedText)
            };
            // Open the work item creation panel
            this.context.pageContext.getService<IWorkItemService>("work-item-service").createWorkItem(witType, undefined, options, initialValues);
        }
    };

    private _addSplitTiming(performanceService: IVssPerformanceService, timingName: string): void {
        performanceService.addSplitTiming(timingName, PerformanceConstants.WitEmbed);
    }
}

VssComponent.register("Wiki.CreateWitMenu", CreateWitMenu);
